import { useState, useEffect } from 'react';
import Api from '../Api';

// Helper function to extract permissions synchronously
function getInitialPermissions(user, moduleName) {
  const defaultPermissions = {
    canView: false,
    canCreate: false,
    canEdit: false,
    canDelete: false,
  };

  if (!user) return defaultPermissions;

  // Check for full access (Owner only) - usually role 'users' or undefined/null role treated as owner in some contexts,
  // but here we stick to the explicit check: if NOT subuser, assume owner/full access.
  if (user.role !== 'subuser') {
    return { canView: true, canCreate: true, canEdit: true, canDelete: true };
  }

  // Subuser Logic
  // 1. If permissions array exists, it MUST be respected (even for admins)
  if (user.permissions && Array.isArray(user.permissions) && user.permissions.length > 0) {
    const permission = user.permissions.find(p => p.module === moduleName);
    if (permission) {
      return {
        canView: !!permission.can_view,
        canCreate: !!permission.can_create,
        canEdit: !!permission.can_edit,
        canDelete: !!permission.can_delete,
      };
    }
    // If permissions array exists but module is not found, access is denied for that module
    return defaultPermissions;
  }

  // 2. Fallback: only if NO permissions array exists, allow admin full access
  if (user.access_profile === 'admin') {
    return { canView: true, canCreate: true, canEdit: true, canDelete: true };
  }

  return defaultPermissions;
}

export function usePermission(moduleName) {
  // Initialize state synchronously from localStorage to prevent "Access Denied" flash
  const [permissions, setPermissions] = useState(() => {
    try {
      const storedUser = localStorage.getItem('authUser');
      const user = storedUser ? JSON.parse(storedUser) : null;
      return getInitialPermissions(user, moduleName);
    } catch (e) {
      console.error('Error parsing authUser from localStorage during init', e);
      return {
        canView: false,
        canCreate: false,
        canEdit: false,
        canDelete: false,
      };
    }
  });

  useEffect(() => {
    async function loadPermissions() {
      try {
        const storedUser = localStorage.getItem('authUser');
        let user = storedUser ? JSON.parse(storedUser) : null;
        
        if (!user) {
          setPermissions({ canView: false, canCreate: false, canEdit: false, canDelete: false });
          return;
        }

        // If user is subuser and has NO permissions in local storage, try to fetch from API
        // This handles the case where the user might have been updated remotely or data is incomplete
        if (user.role === 'subuser' && (!user.permissions || user.permissions.length === 0)) {
           try {
              const profileData = await Api.getProfile();
              if (profileData) {
                const newPermissions = profileData.permissions || [];
                // Update localStorage to avoid future fetches
                const updatedUser = { ...user, permissions: newPermissions };
                localStorage.setItem('authUser', JSON.stringify(updatedUser));
                
                // Update state with fetched permissions
                setPermissions(getInitialPermissions(updatedUser, moduleName));
              }
            } catch (err) {
              console.error('Erro ao buscar permissões atualizadas:', err);
            }
        }
      } catch (e) {
        console.error('Error in usePermission effect', e);
      }
    }

    loadPermissions();
  }, [moduleName]);

  return permissions;
}
